import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, MessageCircle, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const LOGO = 'https://customer-assets.emergentagent.com/job_accretion-axiom/artifacts/3rwpkqcm_7a40f4_da18f8518e4c4d5dbeedf37582cf828c~mv2.avif';
const DISC = 'https://customer-assets.emergentagent.com/job_realestate-fund-6/artifacts/qsxu12yq_1_LE_upscale_balanced_x4.webp';

const TOPICS = [
  { key: 'realestate', img: 'https://customer-assets.emergentagent.com/job_accretionaxiom/artifacts/2r1vbapv_Accretion%20Axiom%20Real%20Estate.png', link: '/real-estate', points: 6 },
  { key: 'quant', img: '/images/invest-quant.png', link: '/quantitative/results', points: 3 },
  { key: 'copytrading', img: 'https://static.prod-images.emergentagent.com/jobs/972a2ae2-9c5a-40a4-b89e-5ecb188d7363/images/d666bb08910ac1c7dcb209a99ddc2ea66bf8bdd01f2798946fb13ac5ca80b5c8.jpeg', link: null, points: 3 },
  { key: 'funding', img: 'https://customer-assets-eiarnc6j.emergentagent.net/job_972a2ae2-9c5a-40a4-b89e-5ecb188d7363/artifacts/a48x3zn2_image.png', link: null, points: 3, fit: 'contain', verifyUrl: 'https://app.fundingpips.com/certificates/verify/eaf4ece8-a831-47d8-b663-7ea42870bb16' },
  { key: 'airbnb', img: 'https://static.prod-images.emergentagent.com/jobs/972a2ae2-9c5a-40a4-b89e-5ecb188d7363/images/724cafa0d3e7070eb686fc0361ca70c8e55ea4323d687b36d9e50152482f04f5.jpeg', link: null, points: 4 },
];

const TOTAL_SLIDES = 9; // 0 cover, 1 index, 2-6 topics, 7 summary, 8 CTA

const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

const InvestPage = () => {
  const { t, lang, setLang } = useLanguage();
  const [slide, setSlide] = useState(0);
  const [details, setDetails] = useState(null);
  const wheelLock = useRef(false);
  const touchStart = useRef(null);

  const goTo = useCallback((n) => setSlide(Math.max(0, Math.min(TOTAL_SLIDES - 1, n))), []);
  const next = useCallback(() => goTo(slide + 1), [slide, goTo]);
  const prev = useCallback(() => goTo(slide - 1), [slide, goTo]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'PageDown') { e.preventDefault(); next(); }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp' || e.key === 'PageUp') { e.preventDefault(); prev(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);

  const onWheel = (e) => {
    if (details || wheelLock.current) return;
    if (Math.abs(e.deltaY) < 20) return;
    wheelLock.current = true;
    e.deltaY > 0 ? next() : prev();
    setTimeout(() => { wheelLock.current = false; }, 700);
  };

  const onTouchStart = (e) => { if (details) return; touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const diffX = touchStart.current.x - e.changedTouches[0].clientX;
    const diffY = touchStart.current.y - e.changedTouches[0].clientY;
    if (Math.abs(diffX) > 50 && Math.abs(diffX) > Math.abs(diffY)) diffX > 0 ? next() : prev();
    touchStart.current = null;
  };

  return (
    <div
      className="w-screen overflow-hidden text-white relative"
      style={{ height: '100dvh', backgroundColor: '#0b1017' }}
      onWheel={onWheel}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="invest-deck"
    >
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&display=swap" rel="stylesheet" />
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-6 md:px-10 pt-6 pb-3">
        <Link to="/" className="flex items-center gap-3" data-testid="invest-logo-link">
          <img src={LOGO} alt="AAC" className="h-9 w-auto" />
          <span className="text-white text-base md:text-xl font-semibold hidden sm:inline" style={SERIF}>Accretion Axiom Capital</span>
        </Link>
        <div className="flex items-center gap-1">
          {['en', 'es'].map(l => (
            <button key={l} onClick={() => setLang(l)} className={`px-2.5 py-1 rounded text-xs font-bold transition-colors ${lang === l ? 'bg-white text-black' : 'text-gray-500 hover:text-white'}`} data-testid={`invest-lang-${l}`}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Slides track */}
      <div
        className="flex h-full transition-transform duration-500 ease-in-out"
        style={{ transform: `translateX(-${slide * 100}vw)` }}
      >
        {/* Slide 1 — Cover with disc */}
        <Slide>
          <img src={DISC} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 pointer-events-none" style={{ filter: 'invert(1)' }} />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950/80 via-gray-950/30 to-gray-950/95 pointer-events-none"></div>
          <div className="text-center relative z-10 px-6">
            <img src={LOGO} alt="Accretion Axiom Capital" className="h-14 md:h-24 w-auto mx-auto mb-6 md:mb-8" />
            <p className="text-gray-300 text-xs md:text-sm tracking-[0.4em] mb-3 md:mb-4" style={{ paddingLeft: '0.4em' }}>{t('invest.cover.tag')}</p>
            <h1 className="text-3xl sm:text-5xl lg:text-7xl font-semibold mb-5 md:mb-8" style={SERIF}>Accretion Axiom Capital</h1>
            <p className="text-gray-300 text-sm md:text-lg max-w-xl mx-auto">{t('invest.cover.subtitle')}</p>
            <p className="text-gray-500 text-xs md:text-sm mt-8 md:mt-12">{t('invest.cover.hint')}</p>
          </div>
        </Slide>

        {/* Slide 2 — Index (uniform, serious) */}
        <Slide scroll>
          <div className="w-full max-w-3xl px-5 md:px-6">
            <p className="text-gray-500 text-xs md:text-sm tracking-[0.3em] mb-3 md:mb-4">{t('invest.index.tag')}</p>
            <h2 className="text-2xl md:text-5xl font-semibold mb-6 md:mb-12" style={SERIF}>{t('invest.index.title')}</h2>
            <div className="space-y-2.5 md:space-y-4">
              {TOPICS.map((topic, i) => (
                <button
                  key={topic.key}
                  onClick={() => goTo(i + 2)}
                  className="w-full flex items-center gap-4 md:gap-6 border border-gray-800 bg-gray-900/60 rounded-lg px-4 md:px-6 py-3.5 md:py-5 text-left hover:border-gray-500 transition-colors"
                  data-testid={`index-item-${topic.key}`}
                >
                  <span className="font-mono text-xs md:text-sm text-gray-500">0{i + 1}</span>
                  <span className="font-semibold text-sm md:text-lg flex-1">{t(`invest.${topic.key}.title`)}</span>
                  <ArrowRight size={16} className="text-gray-600" />
                </button>
              ))}
            </div>
          </div>
        </Slide>

        {/* Slides 3-7 — Topics */}
        {TOPICS.map((topic, i) => (
          <Slide key={topic.key} scroll>
            <div className="w-full max-w-6xl px-5 md:px-10">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-14 items-start md:items-center mb-5 md:mb-10">
                {/* Text */}
                <div className="md:col-span-3">
                  <div className="mb-2.5 md:mb-0">
                    <h2 className="text-2xl md:text-5xl font-semibold mb-1 md:mb-4 leading-tight" style={SERIF}>
                      <span style={{ color: '#d6b36a' }}>0{i + 1}</span>
                      <span className="text-gray-600 mx-2 md:mx-4">·</span>
                      {t(`invest.${topic.key}.title`)}
                    </h2>
                    <p className="text-sm md:text-lg font-medium whitespace-pre-line leading-snug md:mb-5" style={{ color: '#d6b36a' }}>{t(`invest.${topic.key}.subtitle`)}</p>
                  </div>
                  <p className="text-gray-400 text-[13px] md:text-base leading-snug md:leading-relaxed mb-2.5 md:mb-6 line-clamp-2 md:line-clamp-none">{t(`invest.${topic.key}.para`)}</p>
                  <button
                    onClick={() => setDetails(topic)}
                    className="md:hidden inline-flex items-center gap-2 text-[13px] text-white border border-gray-700 rounded-full px-4 py-1.5 mb-1 active:bg-gray-800 transition-colors"
                    data-testid={`details-btn-${topic.key}`}
                  >
                    {t('invest.details')} <ArrowRight size={13} />
                  </button>
                  <div className="hidden md:block space-y-1 md:space-y-3">
                    {Array.from({ length: topic.points }, (_, n) => n + 1).map(n => (
                      <div key={n} className="flex items-start gap-2.5 md:gap-3">
                        <Check size={14} className="text-gray-500 flex-shrink-0 mt-0.5" />
                        <p className="text-gray-300 text-[13px] md:text-sm leading-snug md:leading-relaxed">{t(`invest.${topic.key}.p${n}`)}</p>
                      </div>
                    ))}
                  </div>
                  {topic.link && (
                    <a href={topic.link} target="_blank" rel="noopener noreferrer" className="hidden md:inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white underline underline-offset-4 mt-2.5 md:mt-6 transition-colors" data-testid={`slide-link-${topic.key}`}>
                      {t('invest.moreInfo')} <ArrowRight size={14} />
                    </a>
                  )}
                </div>
                {/* Photo */}
                <div className="flex md:col-span-2 flex-col items-center gap-1.5 md:gap-3">
                  {topic.fit === 'contain' ? (
                    <img src={topic.img} alt={t(`invest.${topic.key}.title`)} className="h-60 md:h-[22rem] w-auto object-contain rounded-xl shadow-2xl shadow-black/70 ring-1 ring-white/10" data-testid={`slide-img-${topic.key}`} />
                  ) : (
                    <img src={topic.img} alt={t(`invest.${topic.key}.title`)} className="w-full h-48 md:h-72 object-cover rounded-xl shadow-2xl shadow-black/70 ring-1 ring-white/10" data-testid={`slide-img-${topic.key}`} />
                  )}
                  {topic.verifyUrl && (
                    <a href={topic.verifyUrl} target="_blank" rel="noopener noreferrer" className="text-green-400 text-xs md:text-sm hover:text-green-300 underline underline-offset-4 transition-colors" data-testid={`verify-link-${topic.key}`}>
                      {t('invest.verify')} ↗
                    </a>
                  )}
                </div>
              </div>
              {/* Stat cards */}
              <div className="grid grid-cols-3 gap-2 sm:gap-5 max-w-4xl mx-auto" data-testid={`stat-cards-${topic.key}`}>
                {[1, 2, 3].map(n => (
                  <div key={n} className="border border-gray-800 bg-gray-900 rounded-lg px-2 py-3 sm:p-6 min-h-[104px] sm:min-h-[125px] flex flex-col items-center justify-start gap-1.5 sm:gap-3">
                    <p className="text-gray-500 text-[9px] md:text-xs uppercase tracking-widest text-center leading-tight">{t(`invest.${topic.key}.c${n}l`)}</p>
                    <div className="flex-1 flex items-center justify-center">
                      <p className="text-white text-[13px] md:text-2xl font-bold leading-snug text-center">{t(`invest.${topic.key}.c${n}v`)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Slide>
        ))}

        {/* Slide 8 — Summary */}
        <Slide scroll>
          <div className="w-full max-w-5xl px-5 md:px-6">
            <p className="text-gray-500 text-xs md:text-sm tracking-[0.3em] mb-3 md:mb-4">{t('invest.summary.tag')}</p>
            <h2 className="text-2xl md:text-5xl font-semibold mb-3 md:mb-10" style={SERIF}>{t('invest.summary.title')}</h2>
            {/* Mobile: 1 column cards, content in 2 columns (label / value) */}
            <div className="space-y-1.5 md:hidden" data-testid="summary-table">
              {TOPICS.map((topic, i) => (
                <button
                  key={topic.key}
                  onClick={() => goTo(i + 2)}
                  className="w-full border border-gray-800 bg-gray-900/60 rounded-lg px-4 py-2 text-left hover:border-gray-500 transition-colors"
                  data-testid={`summary-row-${topic.key}`}
                >
                  <span className="block font-semibold text-[13px] leading-snug mb-1">{t(`invest.${topic.key}.title`)}</span>
                  <div className="space-y-1">
                    {[1, 2, 3].map(n => (
                      <div key={n} className="flex items-baseline justify-between gap-3">
                        <span className="text-gray-500 text-[9px] uppercase tracking-widest">{t(`invest.${topic.key}.c${n}l`)}</span>
                        <span className="text-white text-[12px] font-bold leading-tight text-right">{t(`invest.${topic.key}.c${n}v`)}</span>
                      </div>
                    ))}
                  </div>
                </button>
              ))}
            </div>
            {/* Desktop: original table rows */}
            <div className="hidden md:block space-y-3">
              {TOPICS.map((topic, i) => (
                <button
                  key={topic.key}
                  onClick={() => goTo(i + 2)}
                  className="w-full grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-6 items-center border border-gray-800 bg-gray-900/60 rounded-lg px-6 py-4 text-left hover:border-gray-500 transition-colors"
                  data-testid={`summary-row-desktop-${topic.key}`}
                >
                  <span className="font-semibold text-base">{t(`invest.${topic.key}.title`)}</span>
                  {[1, 2, 3].map(n => (
                    <span key={n} className="flex flex-col">
                      <span className="text-gray-500 text-[10px] uppercase tracking-widest">{t(`invest.${topic.key}.c${n}l`)}</span>
                      <span className="text-white text-base font-bold">{t(`invest.${topic.key}.c${n}v`)}</span>
                    </span>
                  ))}
                </button>
              ))}
            </div>
          </div>
        </Slide>

        {/* Slide 9 — CTA */}
        <Slide scroll>
          <div className="text-center relative z-10 px-6">
            <p className="text-gray-500 text-xs md:text-sm tracking-[0.3em] mb-3 md:mb-4">{t('invest.cta.tag')}</p>
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-semibold mb-5 md:mb-8" style={SERIF}>{t('invest.cta.title')}</h2>
            <p className="text-gray-400 text-sm md:text-lg max-w-xl mx-auto mb-8 md:mb-12">{t('invest.cta.subtitle')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://wa.me/34633905071" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-white text-black px-8 py-4 font-bold rounded-lg hover:bg-gray-200 transition-colors" data-testid="invest-contact-btn">
                <MessageCircle size={18} /> {t('invest.cta.contact')}
              </a>
              <a href="/quantitative/results" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 border border-gray-700 px-8 py-4 font-bold rounded-lg hover:border-gray-500 transition-colors" data-testid="invest-results-btn">
                {t('invest.cta.results')} <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </Slide>
      </div>

      {/* Mobile details sheet */}
      {details && (
        <div className="fixed inset-0 z-50 md:hidden flex items-center justify-center bg-black/85 p-5" onClick={() => setDetails(null)} data-testid="details-sheet">
          <div className="w-full bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl shadow-black max-h-[80vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 className="text-xl font-semibold leading-snug" style={SERIF}>{t(`invest.${details.key}.title`)}</h3>
              <button onClick={() => setDetails(null)} className="p-1 text-gray-400" data-testid="details-close-btn"><X size={20} /></button>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-5">{t(`invest.${details.key}.para`)}</p>
            <div className="space-y-3">
              {Array.from({ length: details.points }, (_, n) => n + 1).map(n => (
                <div key={n} className="flex items-start gap-3">
                  <Check size={15} className="text-gray-500 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-300 text-sm leading-relaxed">{t(`invest.${details.key}.p${n}`)}</p>
                </div>
              ))}
            </div>
            {details.link && (
              <a href={details.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-gray-300 underline underline-offset-4 mt-6" data-testid={`details-link-${details.key}`}>
                {t('invest.moreInfo')} <ArrowRight size={14} />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Bottom bar — controls centered + page number, same height */}
      <div className="absolute bottom-6 md:bottom-10 left-0 right-0 z-30 px-4 md:px-10 flex items-center justify-between gap-2">
        <div className="w-14 md:w-40" />
        <div className="flex items-center gap-3 md:gap-5 md:mt-5">
          <button onClick={prev} disabled={slide === 0} className="p-2 md:p-2.5 rounded-full border border-gray-700 hover:border-gray-400 disabled:opacity-30 transition-colors" data-testid="prev-slide-btn">
            <ArrowLeft size={15} />
          </button>
          <div className="flex gap-1.5 md:gap-2">
            {Array.from({ length: TOTAL_SLIDES }).map((_, i) => (
              <button key={i} onClick={() => goTo(i)} className={`h-2 rounded-full transition-all ${i === slide ? 'w-5 md:w-6 bg-white' : 'w-1.5 md:w-2 bg-gray-700 hover:bg-gray-500'}`} data-testid={`slide-dot-${i}`} />
            ))}
          </div>
          <button onClick={next} disabled={slide === TOTAL_SLIDES - 1} className="p-2 md:p-2.5 rounded-full border border-gray-700 hover:border-gray-400 disabled:opacity-30 transition-colors" data-testid="next-slide-btn">
            <ArrowRight size={15} />
          </button>
        </div>
        <div className="w-14 md:w-40 flex items-baseline justify-end gap-1 pointer-events-none" data-testid="slide-counter">
          <span className="text-white text-3xl md:text-7xl font-semibold leading-none" style={SERIF}>{String(slide + 1).padStart(2, '0')}</span>
          <span className="text-gray-500 text-[10px] md:text-3xl" style={SERIF}>/ {String(TOTAL_SLIDES).padStart(2, '0')}</span>
        </div>
      </div>
    </div>
  );
};

const Slide = ({ children, scroll }) => (
  scroll ? (
    <div className="w-screen h-full flex-shrink-0 relative overflow-hidden">
      <div className="absolute inset-x-0 top-[4.5rem] bottom-[6.5rem] md:top-24 md:bottom-24 overflow-y-auto overflow-x-hidden overscroll-contain">
        <div className="min-h-full flex items-center justify-center py-2 md:py-4">{children}</div>
      </div>
    </div>
  ) : (
    <div className="w-screen h-full flex-shrink-0 flex items-center justify-center relative overflow-hidden">
      {children}
    </div>
  )
);

export default InvestPage;
